import { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Compass, MapPin, Quote, HeartHandshake, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskedLine, FadeUp, Eyebrow } from "@/components/Reveal";
import { MarqueeRibbon } from "@/components/MarqueeRibbon";
import { countries } from "@/data/countries";

const heroImg =
  "https://images.unsplash.com/photo-1688096038129-fefeb1ffc7cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtdXNsaW0lMjB3b21hbiUyMHRyYXZlbGluZ3xlbnwwfHx8fDE3ODg3OTQ0ODF8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Hijrah Mom — A Field Guide for American Muslim Mothers Moving Abroad"
        siteName="Hijrah Mom"
        description="Country profiles, an interactive map, a step-by-step leaving guide, and one mother's honest opinions — everything an American Muslim family needs to plan hijrah to a Muslim-majority country."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Hijrah Mom",
          description: "A field guide for American Muslim women and mothers planning hijrah to Muslim-majority countries.",
        }}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center bg-cream grain overflow-hidden pt-24 lg:pt-28">
        <span className="absolute -right-10 top-16 font-serif text-[22rem] leading-none text-outline-pink opacity-40 select-none pointer-events-none hidden lg:block" aria-hidden="true">
          هجرة
        </span>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
          <div className="lg:col-span-7 relative z-10">
            <FadeUp>
              <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
                <img src="/logo.png" alt="Hijrah Mom" data-testid="home-logo" className="h-36 w-36 sm:h-44 sm:w-44 lg:h-52 lg:w-52 rounded-full shadow-xl shrink-0" />
                <div className="max-w-xl flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="hijrah-definition">
                  <span className="font-serif italic text-3xl lg:text-4xl text-ink leading-none">hijrah</span>
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-pine-dark">noun · from hajara, "to migrate"</span>
                  <span className="text-ink leading-relaxed text-[15px]">— leaving a place for the sake of Allah, to live where one can practice the deen freely and raise a family in faith.</span>
                </div>
              </div>
            </FadeUp>
            <h1 className="mt-8 font-serif font-bold text-ink tracking-tight text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.08]" data-testid="hero-headline">
              <MaskedLine delay={0.15}>Hijrah isn't running{'\u00A0'}away{'\u00A0'}—</MaskedLine>
              <MaskedLine delay={0.3}>it's finding a place where</MaskedLine>
              <MaskedLine delay={0.45} className="italic text-petal">you feel like you belong.</MaskedLine>
            </h1>
            <FadeUp delay={0.7} className="mt-8 max-w-xl">
              <p className="text-base sm:text-lg text-sage leading-relaxed">
                Hijrah Mom is a lovingly honest guide for American Muslim women and mothers planning the move of a lifetime — real visa facts, real budgets, real cities, and one mom's unfiltered opinions on where to raise your family in the Muslim world.
              </p>
            </FadeUp>
            <FadeUp delay={0.85} className="mt-10 flex flex-wrap gap-4">
              <button
                data-testid="hero-quiz-cta"
                onClick={() => navigate("/quiz")}
                className="inline-flex items-center gap-2 bg-petal text-white font-semibold px-7 py-3.5 rounded-full shadow-lg shadow-petal/30 hover:bg-pine hover:shadow-pine/30 transition-colors duration-300"
              >
                <Compass size={17} /> Find My Country
              </button>
              <button
                data-testid="hero-map-cta"
                onClick={() => navigate("/map")}
                className="inline-flex items-center gap-2 border border-pine/30 text-pine font-semibold px-7 py-3.5 rounded-full hover:bg-blush-deep hover:border-petal/40 transition-colors duration-300"
              >
                <MapPin size={17} /> Explore the Map
              </button>
            </FadeUp>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="clip-arch overflow-hidden shadow-2xl shadow-petal/25 border-4 border-white">
                <img src={heroImg} alt="American Muslim woman in a pink hijab walking through an airport terminal" className="w-full h-[420px] lg:h-[520px] object-cover" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.7 }}
                className="absolute left-2 lg:-left-12 bottom-10 bg-white rounded-3xl shadow-2xl border border-pine/10 px-7 py-6 lg:px-9 lg:py-7 animate-float-slow"
                data-testid="hero-float-card"
              >
                <p className="font-mono text-xs tracking-[0.3em] uppercase text-petal">Bismillah</p>
                <p className="font-serif italic text-2xl lg:text-3xl text-pine mt-1.5 whitespace-nowrap">Next destination: Cairo.</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <MarqueeRibbon />

      {/* MOM'S TAKES */}
      <section className="py-20 lg:py-28 bg-cream grain relative" data-testid="home-opinions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="max-w-2xl mb-14">
            <Eyebrow>Mom's unfiltered takes</Eyebrow>
            <h2 className="mt-4 font-serif font-semibold text-ink text-3xl lg:text-5xl leading-tight">
              Passion for travel, <span className="italic text-petal">mission hijrah.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {["malaysia", "turkiye"].map((slug) => countries.find((c) => c.slug === slug)).map((c, i) => (
              <FadeUp key={c.slug} delay={i * 0.1} className="h-full">
                <Link
                  to={`/countries/${c.slug}#mom-take`}
                  data-testid={`home-opinion-${c.slug}`}
                  className="block h-full bg-white rounded-2xl border border-pine/10 p-7 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300"
                >
                  <Quote size={22} className="text-petal" />
                  <p className="mt-4 font-serif italic text-xl text-ink leading-relaxed">
                    "{c.mom.homeTake || `${c.mom.verdict.split(".")[0]}.`}"
                  </p>
                  <div className="mt-6 flex items-center justify-between border-t border-pine/10 pt-4">
                    <span className="text-sm font-semibold text-pine">{c.name}</span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SAFETY NOTE */}
      <section className="bg-cream border-t border-petal/20 py-12 lg:py-14" data-testid="home-safety-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="bg-white rounded-3xl border border-petal/30 shadow-sm p-6 lg:p-8 flex flex-col md:flex-row gap-6 md:items-center">
            <span className="w-12 h-12 rounded-full bg-petal text-white flex items-center justify-center shrink-0"><HeartHandshake size={22} /></span>
            <div className="flex-1">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-petal">Sister, you are not alone</p>
              <h2 className="mt-1.5 font-serif font-semibold text-ink text-xl lg:text-2xl leading-snug">
                If you are experiencing domestic violence, your safety comes before any plan on this site.
              </h2>
              <p className="mt-2 text-sm text-sage leading-relaxed">Help is free, confidential, and available 24/7. Where to turn:</p>
              <ul className="mt-3 space-y-2 text-sm text-ink/85 leading-relaxed" data-testid="dv-help-options">
                <li className="flex gap-2.5"><span className="text-petal mt-0.5">✦</span><span><span className="font-semibold text-pine">US National Domestic Violence Hotline</span> — call or text any hour, from anywhere (buttons to the right).</span></li>
                <li className="flex gap-2.5"><span className="text-petal mt-0.5">✦</span><span><span className="font-semibold text-pine">Your local masjid or Islamic center</span> — many have sisters' committees, emergency housing contacts, and imams who will help quietly and without judgment.</span></li>
                <li className="flex gap-2.5"><span className="text-petal mt-0.5">✦</span><span><a href="https://www.solaceuk.org" target="_blank" rel="noopener noreferrer" data-testid="dv-solace-link" className="font-semibold text-pine underline underline-offset-4 hover:text-petal transition-colors">Solace (solaceuk.org)</a> — UK-based Muslim women's charity that offers remote support internationally.</span></li>
              </ul>
            </div>
            <div className="flex flex-col gap-2 shrink-0" data-testid="dv-hotline">
              <a href="tel:18007997233" data-testid="dv-hotline-call" className="inline-flex items-center justify-center gap-2 bg-pine text-cream text-sm font-semibold px-5 py-3 rounded-full hover:bg-pine-dark transition-colors">
                <Phone size={15} /> 1-800-799-7233
              </a>
              <a href="sms:88788&body=START" data-testid="dv-hotline-text" className="inline-flex items-center justify-center gap-2 border border-pine/25 text-pine text-sm font-semibold px-5 py-3 rounded-full hover:bg-blush-deep transition-colors">
                Text "START" to 88788
              </a>
              <a href="https://www.thehotline.org" target="_blank" rel="noopener noreferrer" data-testid="dv-hotline-link" className="text-center text-xs font-semibold text-petal hover:text-pine underline underline-offset-4">
                National Domestic Violence Hotline · thehotline.org
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
